import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { useIsQuickRequest } from "@RequestFormV2/context/requestFormMode";
import { usePersistDraft } from "@RequestFormV2/hooks/usePersistDraft";
import { useSchedule } from "@RequestFormV2/sections/Schedule/context";
import { collapseStopsToClassicTwo } from "@RequestFormV2/sections/Schedule/utils/collapseStopsToClassicTwo";

type ReplaceStops = (stops: ScheduleStopValue[]) => void;

/**
 * When the payment location's schedule options disable multistop, strip extra
 * stops back to first pickup + last delivery (V1 switches to classic pickup/delivery UI).
 */
export function useCollapseExtraStopsWhenMultistopDisabled(replaceStops: ReplaceStops) {
  const { getValues, setValue } = useFormContext();
  const isQuickRequest = useIsQuickRequest();
  const { scheduleOptions, isLoadingScheduleOptions } = useSchedule();
  const persistDraft = usePersistDraft();
  const previousMultiStopsEnabled = useRef<boolean | undefined>(undefined);

  const multiStopsEnabled = isQuickRequest ? false : scheduleOptions?.multi_stops_enabled;

  useEffect(() => {
    if (isLoadingScheduleOptions || multiStopsEnabled === undefined) return;

    const wasEnabled = previousMultiStopsEnabled.current;
    previousMultiStopsEnabled.current = multiStopsEnabled;

    // Collapse when multistop is off: either after a toggle from ON→OFF, or on
    // first resolved options for a location that never had multistop (draft/template).
    const shouldCollapse =
      multiStopsEnabled === false && (wasEnabled === true || wasEnabled === undefined);
    if (!shouldCollapse) return;

    const stops = getValues("schedule.stops") as ScheduleStopValue[] | undefined;
    const collapsed = collapseStopsToClassicTwo(stops);
    if (!collapsed) return;

    replaceStops(collapsed);
    setValue("schedule.multistop_instructions", "", { shouldDirty: true });
    // WEB-11639: classic 2-stop schedule must not keep packing-list stop keys.
    const packingList = (getValues("packing_list") as unknown[]) || [];
    packingList.forEach((_, index) => {
      setValue(`packing_list.${index}.pickup_stop_key`, undefined, { shouldDirty: true });
      setValue(`packing_list.${index}.delivery_stop_key`, undefined, { shouldDirty: true });
    });
    persistDraft("schedule");
    persistDraft("packing_list");
  }, [
    multiStopsEnabled,
    isLoadingScheduleOptions,
    getValues,
    replaceStops,
    setValue,
    persistDraft,
  ]);
}
